/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { App, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { TextInput } from 'src/components/ts-components/TextInput'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'

const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    // backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type DivisionCloneModalProps = {
  isOpen: boolean
  onClose: () => void
  division?: any
  refetch: (shouldRefresh: boolean) => void
}

const DivisionCloneModal = ({
  isOpen,
  onClose,
  division,
  refetch,
}: DivisionCloneModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [divisionName, setDivisionName] = useState('')
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()
  const [isLoading, setIsLoading] = useState(false)
  const handleSaveDivisionClone = useCallback(async () => {
    try {
      setIsLoading(true)
      console.log(division)
      await req(`/registration/${division.id}/admin/division/clone`, {
        method: 'POST',
        body: JSON.stringify({
          division_name: divisionName,
        }),
      })
      message.success(`${divisionName} Created Successfully`)
      setDivisionName('')
      onClose()
      refetch(false)
    } catch (error: any) {
      message.error(error.message || 'Failed to Clone Division')
    } finally {
      setIsLoading(false)
    }
  }, [division.scheduleId, divisionName, message, onClose])

  const handleCancelDivisionClone = useCallback(() => {
    setDivisionName('')
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button
          text='Cancel'
          onClick={handleCancelDivisionClone}
          variant='secondary'
        />
        <Button
          text='Save'
          onClick={handleSaveDivisionClone}
          variant='primary'
          disabled={!divisionName || isLoading}
        />
      </div>
    )
  }, [
    divisionName,
    handleCancelDivisionClone,
    handleSaveDivisionClone,
    isLoading,
    isMobile,
  ])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Clone Division
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
          cursor: pointer;
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '770px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : 500}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={handleCancelDivisionClone}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={modalStyles}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Text variant='paragraphMedium' color={colors.WHITE}>
          What would you like to name the new division?
        </Text>
        <TextInput
          label='Division Name'
          value={divisionName}
          setValue={(e) => setDivisionName(e)}
          name='divisionName'
          fieldErrors={[]}
          customError={''}
        />
      </div>
    </Modal>
  )
}

export default DivisionCloneModal
